import { Icon, IconButton } from '@material-ui/core';
import { UseStore } from 'zustand';
import { getWorksheetTypes, UserProfile } from '../../api/user-profile-api';
import { Visit, worksheetToVisit } from '../../core';
import { AuditBatch, auditBatch } from '../../core/audit-batch-api';
import { coderProductivity } from '../../core/coder-productivity';
import { formatLocalDate, stringToDate } from '../../core/date-time';
import { documentResolver } from '../../core/document-resolver';
import { personName } from '../../core/format';
import {
  OrganizationApi,
  organizationApi,
  organizationScope,
  OrganizationScope
} from '../../core/organization-api';
import {
  createPageApi,
  PageApi,
  TableColumn,
  TableProps
} from '../../core/paged-table';
import { history } from '../../core/router';
import { getOrgUrlParams } from '../../core/url-params';
import {
  getCodingConfigRequest,
  hospitalistEnabled,
  setCurrentVisitSession
} from '../../core/visit-context';
import {
  auditIdField,
  auditReviewAccountField,
  auditReviewAuditedField,
  auditReviewAuditorField, auditReviewIdField, auditReviewProviderField,
  auditServiceDateField,
  auditTypeField,
  chartOwnerLastField,
  locationField,
  mrnField,
  ownerLastField,
  patientLastField,
  serviceDateField,
  visitAccountField,
  visitDepartmentField,
  visitFirstDateInCodingField,
  visitInsuranceField,
  worksheetAuditDateField,
  worksheetStateField,
  worksheetTypeField
} from '../../core/visit-queue';
import { visitSearchApi, VisitSearchApi } from '../../core/visit-search-api';
import {
  currentWorkflow,
  isAuditWorkflow, workflows
} from '../../core/workflow-api';
import { AccountLink, AgeInQueue, WorksheetIcon } from './coding-status';
import { resolveStateColumns } from './column-resolver';
import { FacilityWorksheetIcon } from './facility-status';

const params = getOrgUrlParams();

function stateColumns(
  facility = false,
  inOut = false,
  col: TableColumn[] = null
): TableColumn[] {
  if (!facility) {
    return [
      {
        label: 'State',
        sort: true,
        render: (r: Visit) => WorksheetIcon(r.wsSummaries[0]),
        style: { width: 90 },
        data: worksheetStateField,
      },
    ];
  }

  if (col?.length) {
    return col;
  }
  const columns = [
    {
      label: 'Profee',
      sort: true,
      render: (r: Visit) => (
        <FacilityWorksheetIcon
          {...r.wsSummaries.filter((v) => v.worksheetType === 'PHYSICIAN')[0]}
          visit={r}
        />
      ),
      style: { width: 90 },
      data: worksheetStateField,
    },
  ];

  if (inOut) {
    columns.push(
      {
        label: 'In Pt.',
        sort: true,
        render: (r: Visit) => (
          <FacilityWorksheetIcon
            {...r.wsSummaries.filter((v) => v.worksheetType === 'INPATIENT')[0]}
            visit={r}
          />
        ),
        style: { width: 80 },
        data: worksheetStateField,
      },

      {
        label: 'Out Pt.',
        sort: true,
        render: (r: Visit) => (
          <FacilityWorksheetIcon
            {...r.wsSummaries.filter(
              (v) => v.worksheetType === 'OUTPATIENT'
            )[0]}
            visit={r}
          />
        ),
        style: { width: 90 },
        data: worksheetStateField,
      }
    );
  }
  return columns;
}
export const visitColumns = (
  facility = false,
  inOut = false,
  col: TableColumn[] = null
): TableColumn[] => {
  const columns: TableColumn[] = [
    {
      label: 'Age',
      sort: true,
      render: (r) => AgeInQueue(r.firstDateInCoding),
      style: { width: 40, padding: 'default', align: 'center' },
      data: visitFirstDateInCodingField,
    },
  ];

  columns.push(...stateColumns(facility, inOut, col));
  columns.push(
    {
      label: 'Account',
      id: 'accountNumber',
      sort: true,
      style: { width: 90 },
      data: visitAccountField,
    },
    {
      label: 'MRN',
      id: 'mrn',
      sort: true,
      style: { width: 90 },
      data: mrnField,
    },
    {
      label: 'DOS',
      sort: true,
      render: (r) => formatLocalDate(r.serviceDate),
      style: { width: 100 },
      data: serviceDateField,
    },
    {
      label: 'Patient',
      sort: true,
      render: (r) => personName(r.patient),
      style: { width: '15vw' },
      data: patientLastField,
    },
    {
      label: 'Provider',
      sort: true,
      render: (r) => personName(r.wsSummaries[0]?.owningProvider),
      style: { width: '15vw' },
      data: ownerLastField,
    },
    {
      label: 'Insurance',
      sort: true,
      render: (r) => (r.insurances && r.insurances[0]?.companyName) || '' || '',
      style: { width: '17vw' },
      data: visitInsuranceField,
    },
    {
      render: (row) => (
        <IconButton
          color="primary"
          onClick={() => documentResolver.openWindow(row, true)}
        >
          <Icon>description</Icon>
        </IconButton>
      ),

      style: { width: 24, padding: 'none' },
    },
    {
      render: (row) => {
        return (
          <AccountLink
            onClick={() => setCurrentVisitSession(row)}
            route="/tool"
            icon="play_circle_filled"
          />
        );
      },
      style: { width: 24, padding: 'none' },
    }
  );
  return columns;
};

export const reviewColumns = (
  facility = false,
  col: TableColumn[] = null
): TableColumn[] => {
  const columns: TableColumn[] = [
    {
      label: 'Age',
      sort: true,
      render: (r) => AgeInQueue(r.firstDateInCoding),
      style: { width: 40, padding: 'default', align: 'center' },
      data: visitFirstDateInCodingField,
    },
  ];

  columns.push(...stateColumns(facility, false, col));
  columns.push(
    {
      label: 'Owner',
      sort: true,
      render: (r) => personName(r.owner),
      style: { width: 90 },
      data: chartOwnerLastField,
    },
    {
      label: 'Account',
      id: 'accountNumber',
      sort: true,
      style: { width: 90 },
      data: visitAccountField,
    },
    {
      label: 'MRN',
      id: 'mrn',
      sort: true,
      style: { width: 90 },
      data: mrnField,
    },
    {
      label: 'DOS',
      sort: true,
      render: (r) => formatLocalDate(r.serviceDate),
      style: { width: 100 },
      data: serviceDateField,
    },
    {
      label: 'Patient',
      sort: true,
      render: (r) => personName(r.patient),
      style: { width: '15vw' },
      data: patientLastField,
    },
    {
      label: 'Provider',
      sort: true,
      render: (r) => personName(r.wsSummaries[0]?.owningProvider),
      style: { width: '15vw' },
      data: ownerLastField,
    },
    {
      label: 'Insurance',
      sort: true,
      render: (r) => (r.insurances && r.insurances[0]?.companyName) || '' || '',
      style: { width: '17vw' },
      data: visitInsuranceField,
    },
    {
      render: (row) => (
        <IconButton
          color="primary"
          onClick={() => documentResolver.openWindow(row, true)}
        >
          <Icon>description</Icon>
        </IconButton>
      ),

      style: { width: 24, padding: 'none' },
    },
    {
      render: (row) => {
        return (
          <AccountLink
            onClick={() => setCurrentVisitSession(row)}
            route="/tool"
            icon="play_circle_filled"
          />
        );
      },
      style: { width: 24, padding: 'none' },
    }
  );
  return columns;
};

export const auditColumnsOld = (
  facility = false,
  col: TableColumn[] = null
): TableColumn[] => {
  const columns: TableColumn[] = [
    {
      label: 'Age',
      sort: true,
      render: (r) => AgeInQueue(r.firstDateInCoding),
      style: { width: 40, padding: 'default', align: 'center' },
      data: visitFirstDateInCodingField,
    },
  ];

  columns.push(...stateColumns(facility, false, col));

  const writeAuditRuleName = (r) => {
    return (r?.auditInfo?.auditRules?.[0]?.auditRuleName || r?.auditId);
  };

  columns.push(
    {
      label: 'AuditId',
      render: (r) => { return writeAuditRuleName(r.wsSummaries[0]) },
      style: { width: 150 },
      sort: true,
      data: auditIdField,
    },
    {
      label: 'AuditType',
      //  render: (r) => {return (r.wsSummaries[0]?.auditId)},
      sort: true,
      style: { width: 90 },
      data: auditTypeField,
    },
    {
      label: 'WorksheetType',
      render: (r) => { return (r.wsSummaries[0]?.worksheetType) },
      sort: true,
      style: { width: 90 },
      data: worksheetTypeField,
    },
    {
      label: 'Location',
      id: 'locationCode',
      sort: true,
      style: { width: 90 },
      data: locationField
    },
    {
      label: 'Department',
      id: 'departmentCode',
      sort: true,
      style: { width: '15vw' },
      data: visitDepartmentField
    },
    {
      label: 'Audited',
      sort: true,
      render: (r) => personName(r.owner),
      style: { width: 90 },
      data: chartOwnerLastField,
    },
    {
      label: 'Provider',
      sort: true,
      render: (r) => personName(r.wsSummaries[0]?.owningProvider),
      style: { width: '15vw' },
      data: ownerLastField,
    },
    {
      label: 'MRN',
      id: 'mrn',
      sort: true,
      style: { width: 90 },
      data: mrnField,
    },

    {
      label: 'Account',
      id: 'accountNumber',
      sort: true,
      style: { width: 90 },
      data: visitAccountField,
    },
    {
      label: 'DOS',
      sort: true,
      render: (r) => formatLocalDate(r.serviceDate),
      style: { width: 100 },
      data: serviceDateField,
    },
    {
      label: 'Patient',
      sort: true,
      render: (r) => personName(r.patient),
      style: { width: '15vw' },
      data: patientLastField,
    },

    {
      render: (row) => (
        <IconButton
          color="primary"
          onClick={() => documentResolver.openWindow(row, true)}
        >
          <Icon>description</Icon>
        </IconButton>
      ),

      style: { width: 24, padding: 'none' },
    },
    {
      render: (row) => {
        return (
          <AccountLink
            onClick={() => setCurrentVisitSession(row)}
            route="/tool"
            icon="play_circle_filled"
          />
        );
      },
      style: { width: 24, padding: 'none' },
    }
  );
  return columns;
};

export const auditReviewColumns = (
  facility = false,
  col: TableColumn[] = null
): TableColumn[] => {
  const columns: TableColumn[] = [
    {
      label: 'Audit Date',
      sort: true,
      render: (r) => stringToDate(r.createdLocal.toString()),
      style: { width: 120, align: 'center' },
      data: worksheetAuditDateField,
    },
  ];

  const writeAuditRuleName = (r) => {
    return (r?.auditInfo?.auditRules?.[0]?.auditRuleName || r?.auditId);
  };

  columns.push(
    {
      label: 'AuditId',
      render: (r) => { return writeAuditRuleName(r) },
      style: { width: '15vw', padding: 'default' },
      sort: true,
      data: auditReviewIdField,
    },
    {
      label: 'Auditor',
      render: (r) => { return (r?.auditor?.lastName + ', ' + r?.auditor?.firstName) },
      sort: true,
      style: { width: 120, },
      data: auditReviewAuditorField,
    },
    {
      label: 'Audited',
      render: (r) => { return (r.audited?.lastName + ' ' + r.audited?.firstName) },
      sort: true,
      style: { width: 120, },
      data: auditReviewAuditedField,
    },
    {
      label: 'Provider',
      sort: true,
      render: (r) => personName(r.owningProvider),
      style: { width: '15vw', padding: 'default' },
      data: auditReviewProviderField,
    },
    {
      label: 'DOS',
      sort: false,
      render: (r) => formatLocalDate(r.serviceDate),
      style: { width: 100 },
      data: auditServiceDateField,
    },
    // {
    //   label: 'Location',
    //   id: 'locationCode',
    //   sort: true,
    //   style: { width: 90 },
    //   data: auditReviewLocationField
    // },
    // {
    //   label: 'Department',
    //   id: 'departmentCode',
    //   sort: true,
    //   style: { width: '90' },
    //   data: auditReviewDepartmentField
    // },

    {
      label: 'Account',
      id: 'accountNumber',
      sort: true,
      style: { width: 90 },
      data: auditReviewAccountField,
    },
    {
      label: 'Patient',
      sort: false,
      render: (r) => personName(r.codingQueue.patient),
      style: { width: '15vw' },
    },
    {
      label: 'Insurance',
      sort: false,
      render: (r) => (r?.codingQueue?.insurances && r.codingQueue.insurances[0]?.companyName) || '' || '',
      style: { width: '17vw' },
    },
    {
      label: 'Score',
      sort: false,
      render: (r) => r?.totalPossiblePoints != 0 && Math.round(r?.totalPoints / r?.totalPossiblePoints * 100) + ' %',
      style: { width: 50 },
    },


    {
      render: (row) => (
        <IconButton
          color="primary"
          onClick={() => {
            let visit = worksheetToVisit(row);
            visit.patient = row?.codingQueue?.patient;
            visit.insurances = row?.codingQueue?.insurances;
            documentResolver.openWindow(visit);
          }}
        >
          <Icon>description</Icon>
        </IconButton>
      ),

      style: { width: 24, padding: 'none' },
    },
    // {
    //   render: (row) => {
    //     return (
    //       <AccountLink
    //         onClick={() => setCurrentVisitSession(row)}
    //         route="/tool"
    //         icon="play_circle_filled"
    //       />
    //     );
    //   },
    //   style: { width: 24, padding: 'none' },
    // }
  );
  return columns;
};

//TODO: remove dependency on TableProps

export interface VisitListApi extends Omit<TableProps, 'rows'> {
  org: UseStore<OrganizationApi>;
  api: VisitSearchApi;
  sortApi: VisitSearchApi;
  pageApi: PageApi;
  getColumns: () => TableColumn[];
  resetPage: () => void;
  setUser: (a: UserProfile) => void;
  onLogout?: () => void;
}

export function visitListApi(): VisitListApi {
  const pageApi = createPageApi();
  const api = visitSearchApi(pageApi);
  const sortApi = visitSearchApi();

  api.data.setState({ pendingNavigation: !!params.workflow });

  const resetPage = () => {
    if (!api.data.getState().pendingNavigation) {
      api.actions.setQuery({ pageNumber: 0 });
      pageApi.setState({ pageNumber: 0 });
      coderProductivity.getState().update();
    }
  };

  let currentScope: OrganizationScope;

  const loadCodingConfig = async (s?: OrganizationScope) => {
    s = s || currentScope;

    if (!s) {
      return;
    }

    currentScope = s;

    const workflow = currentWorkflow();
    //TODO: a lighter weight provider request
    const response = await getCodingConfigRequest({
      location: s.locationCode,
      department: s.departmentCode || 'ED',
      worksheetTypes: ['PHYSICIAN'],
      workflowNames: [workflow ],
    });

    try {
      if (response?.result) {
        const { providers, workflowStateMap } = response.result;
        if (providers?.length) {
          api.data.setState({ providers });
        }

        if (workflowStateMap) {
          const states = workflowStateMap[workflow];

          if (states?.length) {
            api.data.setState({ stateFilters: states });
          }
        }
      }
    } catch (err) {
      console.log('Coding config loading error', err);
    }
  };

  auditBatch.events.batch((a: AuditBatch) => {
    api.actions.setQuery({ auditId: a?.auditId || undefined });
    api.actions.search();
  });

  let faclilityColumns = false;
  let inOut = false;
  organizationScope.subscribe(async (s) => {
    resetPage();

    const workflow = currentWorkflow();
    workflows.actions.load({
      location: s.locationCode,
      department: s.departmentCode || 'ED', // TODO allow department change
      workflow: (workflow ) as any,
      activity: 'CODING', // TODO manage audit request
    });

    if (workflow === 'auditStd') {
      auditBatch.actions.clearBatch({
        locationCode: s.locationCode,
        departmentCode: s.departmentCode,
      });
    }

    faclilityColumns = hospitalistEnabled(s);
    inOut = s.locationCode.indexOf('PREMIER') > -1;

    api.actions.setQuery({
      locationCode: s.locationCode,
      departmentCode: s.departmentCode || 'ED',
    });

    loadCodingConfig(s);
    updateWorksheetTypes();
  });

  workflows.events.workflowChanged((a: string) => {
    if (!api.data.getState().pendingNavigation) {
      resetPage();
      api.actions.reset();
      api.actions.search();
    } else {
      api.data.setState({ pendingNavigation: false });
    }

    loadCodingConfig();

    if (a === 'auditStd') {
      const s =
        organizationApi.getState().selection;
      if (s?.department && s?.location) {
        auditBatch.actions.clearBatch({
          locationCode: s.location.code,
          departmentCode: s.department.code,
        });
      }
    }
  });

  let stateColumns: TableColumn[];

  function updateWorksheetTypes() {
    const s = organizationApi.getState().selection;
    if (user && s?.organization && s?.location) {
      const worksheetTypes = getWorksheetTypes(
        user,
        'CODING',
        s.organization.code,
        [s.location.code]
      );
      stateColumns = resolveStateColumns(worksheetTypes);
    }
  }
  let user: UserProfile;
  function setUser(a: UserProfile) {
    user = a;
    updateWorksheetTypes();
  }

  //May cause some problems with loading columns of the review workflow
  function getColumns() {
    return isAuditWorkflow()
      ? auditColumnsOld(faclilityColumns, stateColumns)
      : visitColumns(faclilityColumns, inOut, stateColumns);
  }

  function onLogout() {
    history.push('/logout');
  }

  return {
    sortApi,
    resetPage,
    api,
    getColumns,
    columns: visitColumns(),
    org: organizationApi,
    pageApi,
    onLogout,
    setUser,
  };
}
